import { HttpStatusCode, type AxiosError, type AxiosInstance } from 'axios'

import {
  authProviderAtom,
  type AuthProviderType,
  authStore
} from '@/features/auth/atoms'
import { fetchAuthSession, signOut } from '@/libs/auth/amplify'

import { type ApiInstance } from '../aspida/$api'
import { type ErrorResponse } from '../aspida/@types'
import {
  LOCAL_STORAGE_ACCESS_TOKEN_KEY,
  LOCAL_STORAGE_REFRESH_TOKEN_KEY
} from '../auth/constants'
import { getApiBaseUrlExcludePath } from '../utils'

type ErrorType = ErrorResponse['type']
export const ErrorType = {
  UNAUTHORIZED_MULTI_LOGIN: 'UNAUTHORIZED_MULTI_LOGIN',
  UNAUTHORIZED_INVALID_TOKEN: 'UNAUTHORIZED_INVALID_TOKEN',
  UNAUTHORIZED_EXPIRED_ACCESS_TOKEN: 'UNAUTHORIZED_EXPIRED_ACCESS_TOKEN',
  FORBIDDEN_NOT_IN_LOGIGO_USER_GROUP: 'FORBIDDEN_NOT_IN_LOGIGO_USER_GROUP',
  FORBIDDEN_DISABLED_USER: 'FORBIDDEN_DISABLED_USER'
} as const satisfies Record<ErrorType, ErrorType>

type ErrorHandlerParams = {
  error: AxiosError
  axiosInstance: AxiosInstance
  apiClient: ApiInstance['api']
}

const authErrorHandlers = {
  COGNITO: {
    handle: async ({
      error,
      axiosInstance
    }: ErrorHandlerParams): Promise<AxiosError> => {
      const response = error.response?.data as ErrorResponse
      if (response.type === ErrorType.UNAUTHORIZED_MULTI_LOGIN) {
        await signOut()
        return await Promise.reject(error)
      }

      const session = await fetchAuthSession({ forceRefresh: true })

      const newToken = session?.tokens?.accessToken
      if (newToken && error.config) {
        return await axiosInstance.request(error.config)
      }

      // リフレッシュ処理が失敗 または リフレッシュトークンを保持してない場合は強制ログアウト
      await signOut()
      return await Promise.reject(error)
    },
    logout: async (): Promise<void> => {
      await signOut()
    }
  },
  AUTH0: {
    handle: async ({
      error,
      axiosInstance,
      apiClient
    }: ErrorHandlerParams): Promise<AxiosError> => {
      const logout = () => {
        localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY)
        location.reload()
      }

      const response = error.response?.data as ErrorResponse
      if (response.type === ErrorType.UNAUTHORIZED_MULTI_LOGIN) {
        logout()
        return await Promise.reject(error)
      }

      const refreshApiPath = getApiBaseUrlExcludePath(
        apiClient.auth.refresh.$path()
      )
      const newToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY)

      if (
        newToken &&
        error.config &&
        error.config.url !== refreshApiPath // リフレッシュAPIの場合は無限ループになるので除外
      ) {
        // フロントで保持してるリフレッシュトークンを新しいアクセストークンとして設定して、リフレッシュ処理をリクエスト
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, newToken)
        const { data } = await apiClient.auth.refresh.$put()

        // リフレッシュ処理が成功した場合はトークンを再設定して、認証エラーとなったAPIを再リクエスト
        if (data) {
          localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, data.accessToken)
          localStorage.setItem(
            LOCAL_STORAGE_REFRESH_TOKEN_KEY,
            data.accessToken
          )
          return await axiosInstance.request(error.config)
        }
      }

      // リフレッシュ処理が失敗 または リフレッシュトークンを保持してない場合は、トークンを削除して画面をリロード

      // 未認証の場合に画面をリロードしたくないAPIのパスを定義
      const disabledReloadPaths = [
        // ログインユーザー取得結果を 画面側での認証判定に使用してるので除外（未認証時にリダイレクトが無限ループとなる）
        getApiBaseUrlExcludePath(apiClient.users.me.$path())
      ]

      if (
        error.config?.url &&
        !disabledReloadPaths.includes(error.config.url)
      ) {
        logout()
      }

      return await Promise.reject(error)
    },
    logout: async (): Promise<void> => {
      localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)
      localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY)
      location.reload()
    }
  }
} satisfies Record<
  AuthProviderType,
  {
    handle: (params: ErrorHandlerParams) => Promise<AxiosError>
    logout: () => Promise<void>
  }
>

export const unauthorizedResponseInterceptor = async (
  error: AxiosError,
  axiosInstance: AxiosInstance,
  apiClient: ApiInstance['api']
): Promise<AxiosError> => {
  // 認証エラーの場合はアクセストークンのリフレッシュ処理を試みる
  if (error.response?.status === 401) {
    const authProvider = authStore.get(authProviderAtom)
    if (authProvider) {
      return await authErrorHandlers[authProvider].handle({
        error,
        axiosInstance,
        apiClient
      })
    }
  }

  if (error.response?.status === HttpStatusCode.Forbidden) {
    const response = error.response?.data as ErrorResponse
    if (
      response?.type === ErrorType.FORBIDDEN_NOT_IN_LOGIGO_USER_GROUP ||
      response?.type === ErrorType.FORBIDDEN_DISABLED_USER
    ) {
      const authProvider = authStore.get(authProviderAtom)
      if (authProvider) {
        await authErrorHandlers[authProvider].logout()
      }
    }
  }

  return await Promise.reject(error)
}
